import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Grid, // Using Grid for organized horizontal data placement
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

const COECategories = ['A', 'B', 'C', 'E'];

const COEResultsCard = () => {
  const [latestGroup, setLatestGroup] = useState<any[]>([]);
  const [previousGroup, setPreviousGroup] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // ... (Data Fetching Logic - Unchanged) ...
  const fetchCOEData = async () => {
    setLoading(true);
    try {
      const response = await axios.get<any>(
        'https://staging.workhorse.com.sg/WorkHorseApi/Vehicle/VehicleApiCall',
        {
          headers: {
            'Content-Type': 'application/json',
            'Api-Key': 'FrontEndKey',
          },
        }
      );

      const rawList = response?.data?.responseData?.vehicleApiCallList || [];

      // Grouping and finding latest/previous logic...
      const grouped: Record<string, Record<string, any[]>> = {};
      rawList.forEach((item: any) => {
        if (!grouped[item.month]) grouped[item.month] = {};
        if (!grouped[item.month][item.biddingNo]) grouped[item.month][item.biddingNo] = [];
        grouped[item.month][item.biddingNo].push(item);
      });

      const sortedMonths = Object.keys(grouped).sort((a, b) =>
        new Date(b + '-01').getTime() - new Date(a + '-01').getTime()
      );

      let latest: any[] = [];
      let previous: any[] = [];

      for (let i = 0; i < sortedMonths.length; i++) {
        const month = sortedMonths[i];
        const monthGroup = grouped[month];
        if (monthGroup['2']) {
          latest = monthGroup['2'];
          if (monthGroup['1']) previous = monthGroup['1'];
          else if (sortedMonths[i + 1]) {
            const nextMonthGroup = grouped[sortedMonths[i + 1]];
            previous = nextMonthGroup['2'] || nextMonthGroup['1'] || [];
          }
          break;
        } else if (monthGroup['1']) {
          latest = monthGroup['1'];
          if (sortedMonths[i + 1]) {
            const nextMonthGroup = grouped[sortedMonths[i + 1]];
            previous = nextMonthGroup['2'] || nextMonthGroup['1'] || [];
          }
          break;
        }
      }

      setLatestGroup(latest);
      setPreviousGroup(previous);
    } catch (err) {
      console.error('Error fetching COE data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCOEData();
  }, []);

  // ... (Price and Trend Logic - Unchanged) ...
  const getPremiumAndTrend = (category: string) => {
    const current = latestGroup.find((i) => i.vehicleClass === `Category ${category}`);
    const prev = previousGroup.find((i) => i.vehicleClass === `Category ${category}`);

    if (!current)
      return {
        premium: '-',
        trend: <RemoveIcon fontSize="small" />,
        color: 'text.primary',
      };

    const premiumVal = Number(current.premium);
    const formatted = `$${premiumVal.toLocaleString()}`;

    if (!prev)
      return {
        premium: formatted,
        trend: <RemoveIcon fontSize="small" />,
        color: 'text.primary',
      };

    const prevPremium = Number(prev.premium);

    // Keeping standard logic: Up is often considered "bad" (higher price), Down is "good" (lower price)
    if (premiumVal > prevPremium)
      return {
        premium: formatted,
        trend: <ArrowUpwardIcon fontSize="small" sx={{ color: 'error.main' }} />, // Standard: Red for up
        color: 'error.main',
      };

    if (premiumVal < prevPremium)
      return {
        premium: formatted,
        trend: <ArrowDownwardIcon fontSize="small" sx={{ color: 'success.main' }} />, // Standard: Green for down
        color: 'success.main',
      };

    return {
      premium: formatted,
      trend: <RemoveIcon fontSize="small" />,
      color: 'text.primary',
    };
  };

  const latestLabel = latestGroup[0]
    ? `${new Date(latestGroup[0].month + '-01')
        .toLocaleString('default', { month: 'short', day: '2-digit' }) 
        .toUpperCase()}`
    : '';
    
  const biddingLabel = latestGroup[0]
    ? `${latestGroup[0].biddingNo}${
        latestGroup[0].biddingNo === '1' ? 'st' : 'nd'
      } BIDDING`
    : '';

  // --- Render (Modified for Responsiveness) ---
  return (
    <Box display="flex" flexWrap="wrap" gap={1}>
      <Paper
        elevation={1}
        sx={{
          border: '1px solid #e5e7eb',
          borderRadius: 2,
          p: 2,
          // CRITICAL FIX 1: MaxWidth and flex-grow ensures the card fills the parent Grid
          flex: '1 1 auto', 
          maxWidth: '100%', 
          width: '100%',
          boxSizing: 'border-box',
          
          // Outer Flex Container adjustments:
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 2, sm: 4 }, 
          // Stack the header and data vertically on extra-small screens (phones)
          flexDirection: { xs: 'column', sm: 'row' } 
        }}
      >
        {/* Left Section: Header and Date */}
        <Box sx={{ minWidth: 120, textAlign: { xs: 'center', sm: 'left' } }}> 
          <Typography variant="body1" fontWeight={900} color="text.primary" whiteSpace="nowrap">
            Latest COE Results
          </Typography>
          <Box 
            display="flex" 
            alignItems="center"
            justifyContent={{ xs: 'center', sm: 'flex-start' }} // Center date/bidding on mobile
          >
             <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }} whiteSpace="nowrap">
               {latestLabel} | {biddingLabel}
             </Typography>
             <KeyboardArrowRightIcon fontSize="small" color="secondary" />
          </Box>
        </Box>

        {/* Right Section: COE Data */}
        {loading ? (
          <Box display="flex" justifyContent="center" flexGrow={1}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <Grid 
            container 
            spacing={{ xs: 1, sm: 2 }} // Reduced spacing on mobile
            sx={{ flexGrow: 1 }} 
          >
            {COECategories.map((cat) => {
              const { premium, trend, color } = getPremiumAndTrend(cat);
              return (
                // 💥 CRITICAL FIX 2: Correctly use the 'xs' prop for size.
                // 4 categories. Total columns = 12.
                // xs={3} means 3 columns wide, so 4 items fit in one row (4 * 3 = 12).
                // xs={6} means 6 columns wide, so 2 items fit in one row (2 * 6 = 12).
                <Grid size={{xs:6, sm:3}} key={cat}> 
                  <Box 
                    display="flex" 
                    flexDirection="column" // Stack content vertically for mobile if needed
                    alignItems="center" 
                    gap={0.5}
                    // Added a minimal border/padding for better separation on small screens
                    sx={{ p: 0.5, borderRight: { xs: '1px solid #eee', sm: 'none' }, ':nth-of-type(even)': { borderRight: 'none' } }} 
                  >
                    <Typography variant="body2" fontWeight={600} whiteSpace="nowrap">
                      CATEGORY {cat}
                    </Typography>

                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="body1" fontWeight={600} sx={{ color }} whiteSpace="nowrap" fontSize={{ xs: '0.8rem', sm: '1rem' }}>
                          {premium}
                        </Typography>
                        {trend}
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default COEResultsCard;